// UnsavedChangesContext - Global context for tracking unsaved changes across the application
// Allows components to register/unregister unsaved changes state

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface UnsavedChangesState {
  componentId: string;
  hasChanges: boolean;
}

interface UnsavedChangesContextType {
  /** Check if any component has unsaved changes */
  hasUnsavedChanges: boolean;
  /** Get list of components with unsaved changes */
  getComponentsWithChanges: () => string[];
  /** Register a component's unsaved changes state */
  setComponentUnsavedChanges: (componentId: string, hasChanges: boolean) => void;
  /** Remove component from tracking (e.g., on unmount) */
  removeComponent: (componentId: string) => void;
  /** Clear all unsaved changes (e.g., after successful save or discard) */
  clearAllUnsavedChanges: () => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType | undefined>(undefined);

export interface UnsavedChangesProviderProps {
  children: ReactNode;
}

export const UnsavedChangesProvider: React.FC<UnsavedChangesProviderProps> = ({ children }) => {
  const [componentsState, setComponentsState] = useState<Map<string, boolean>>(new Map());

  const hasUnsavedChanges = Array.from(componentsState.values()).some(hasChanges => hasChanges);

  const getComponentsWithChanges = useCallback(() => {
    return Array.from(componentsState.entries())
      .filter(([_, hasChanges]) => hasChanges)
      .map(([componentId]) => componentId);
  }, [componentsState]);

  const setComponentUnsavedChanges = useCallback((componentId: string, hasChanges: boolean) => {
    setComponentsState(prev => {
      const newState = new Map(prev);
      if (hasChanges) {
        newState.set(componentId, true);
      } else {
        newState.delete(componentId);
      }
      return newState;
    });
  }, []);

  const removeComponent = useCallback((componentId: string) => {
    setComponentsState(prev => {
      const newState = new Map(prev);
      newState.delete(componentId);
      return newState;
    });
  }, []);

  const clearAllUnsavedChanges = useCallback(() => {
    setComponentsState(new Map());
  }, []);

  const contextValue: UnsavedChangesContextType = {
    hasUnsavedChanges,
    getComponentsWithChanges,
    setComponentUnsavedChanges,
    removeComponent,
    clearAllUnsavedChanges,
  };

  return (
    <UnsavedChangesContext.Provider value={contextValue}>
      {children}
    </UnsavedChangesContext.Provider>
  );
};

export const useUnsavedChanges = (): UnsavedChangesContextType => {
  const context = useContext(UnsavedChangesContext);
  if (context === undefined) {
    throw new Error('useUnsavedChanges must be used within an UnsavedChangesProvider');
  }
  return context;
};

// Hook for components to easily register their unsaved changes state
export const useComponentUnsavedChanges = (componentId: string) => {
  const { setComponentUnsavedChanges, removeComponent } = useUnsavedChanges();

  const setHasUnsavedChanges = useCallback((hasChanges: boolean) => {
    setComponentUnsavedChanges(componentId, hasChanges);
  }, [componentId, setComponentUnsavedChanges]);

  const unregisterComponent = useCallback(() => {
    removeComponent(componentId);
  }, [componentId, removeComponent]);

  return {
    setHasUnsavedChanges,
    unregisterComponent,
  };
};